import React, { useState } from "react";
import { FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaPlus } from "react-icons/fa";
import AddressForm from "../../Checkout/steps/components/AddressForm";
import SavedAddressCard from "../../Checkout/steps/components/SavedAddressCard";
import CheckoutEditModal from "../../Checkout/steps/components/CheckoutEditModal";

const CartAddressCard = ({
  selectedAddress,
  defaultAddress,
  userAddresses = [],
  selectedAddressId,
  onSelectAddress,
  onDeleteAddress,
  onEditAddress,
  onAddNewAddress,
  isAuthenticated,
  onRequireAuth,
  addressesLoading,
  pincodeStatus,
  pincodeMessage,
  pincodeData,
}) => {
  const activeAddress = selectedAddress || defaultAddress;
  const isDefault = Boolean(
    activeAddress?.is_default === true ||
    activeAddress?.is_default === 1 ||
    activeAddress?.is_default === "1" ||
    activeAddress?.is_default === "true"
  );
  const [addressExpanded, setAddressExpanded] = useState(true);
  const [isManagingAddress, setIsManagingAddress] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const handleSelect = (addr) => {
    onSelectAddress(addr);
    setIsManagingAddress(false);
    setShowNewAddressForm(false);
  };

  const handleNewSuccess = (savedAddr) => {
    setShowNewAddressForm(false);
    setIsManagingAddress(false);
    onAddNewAddress(savedAddr);
  };

  const handleEditSubmit = async (formData) => {
    await onEditAddress(editingAddress, formData);
    setEditingAddress(null);
  };

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-2">
          <div className="space-y-1 text-xs sm:text-sm text-gray-600">
            <p className="font-semibold text-gray-800">
              Please login to select or add your delivery address
            </p>
            <p className="text-gray-500 text-xs">
              Login is required to check delivery availability and place orders.
            </p>
          </div>
          <button
            type="button"
            onClick={onRequireAuth}
            className="shrink-0 self-start sm:self-center rounded-xl bg-[#046a38] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition hover:bg-[#03522b] active:scale-95"
          >
            Login to Continue
          </button>
        </div>
      );
    }

    if (isManagingAddress) {
      return (
        <div className="space-y-4 pt-1">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
              {showNewAddressForm ? "Add New Address" : "Saved Addresses"}
            </p>
            <button
              onClick={() => {
                setIsManagingAddress(false);
                setShowNewAddressForm(false);
              }}
              className="text-xs font-bold text-[#046a38] hover:underline"
            >
              Done / Close
            </button>
          </div>

          {showNewAddressForm ? (
            <AddressForm
              onSuccess={handleNewSuccess}
              onCancel={() => {
                setShowNewAddressForm(false);
                if (!activeAddress) setIsManagingAddress(false);
              }}
            />
          ) : (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setShowNewAddressForm(true)}
                className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#046a38] bg-emerald-50/50 py-3 text-xs sm:text-sm font-bold text-[#046a38] transition hover:bg-emerald-100/60"
              >
                <FaPlus className="h-3.5 w-3.5" />
                <span>+ Add New Address</span>
              </button>

              <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-1">
                {userAddresses && userAddresses.length > 0 ? (
                  userAddresses.map((addr) => (
                    <SavedAddressCard
                      key={addr.address_id ?? addr.id}
                      addr={addr}
                      selectedId={selectedAddressId || (activeAddress?.address_id ?? activeAddress?.id)}
                      showForm={showNewAddressForm}
                      onSelect={() => handleSelect(addr)}
                      onEdit={() => setEditingAddress(addr)}
                      onDelete={() => onDeleteAddress(addr.address_id ?? addr.id)}
                    />
                  ))
                ) : (
                  <p className="text-center text-xs text-gray-500 py-3">
                    No saved addresses found. Click above to add one.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (!activeAddress) {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-2">
          <div className="space-y-1 text-xs sm:text-sm text-gray-600">
            <p className="font-semibold text-gray-800">No delivery address found</p>
            <p className="text-gray-500 text-xs">
              Please add a delivery address to check delivery availability and proceed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsManagingAddress(true);
              setShowNewAddressForm(true);
            }}
            className="shrink-0 self-start sm:self-center rounded-xl bg-[#046a38] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition hover:bg-[#03522b] active:scale-95"
          >
            + Add Address
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-1.5 text-xs sm:text-sm text-gray-700">
          <div className="flex items-center gap-2 font-bold text-gray-900">
            <span>{activeAddress?.address_type || "Home"}</span>
            {isDefault && (
              <span className="rounded bg-[#046a38] px-1.5 py-0.5 text-[10px] font-bold text-white uppercase tracking-wider">
                DEFAULT
              </span>
            )}
          </div>
          <p className="text-gray-600">
            {activeAddress?.name ? `${activeAddress.name}, ` : ""}
            {activeAddress?.address || activeAddress?.street || ""}
            {activeAddress?.locality ? `, ${activeAddress.locality}` : ""}
            {activeAddress?.city ? `, ${activeAddress.city}` : ""}
            {activeAddress?.pincode ? ` - ${activeAddress.pincode}` : ""}
            {activeAddress?.state ? `, ${activeAddress.state}` : ""}
          </p>
          <p className="text-gray-600 font-medium">
            Mobile: {activeAddress?.mobile || activeAddress?.phone || ""}
          </p>

          {pincodeStatus === "checking" && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium pt-1">
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Checking delivery availability…</span>
            </div>
          )}
          {pincodeStatus === "serviceable" && (
            <div className="flex items-center gap-1.5 text-xs font-bold text-[#046a38] pt-1">
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                <span>✓</span>
                <span>Delivery available {pincodeData?.area_name ? `(${pincodeData.area_name})` : ""}</span>
              </span>
            </div>
          )}
          {(pincodeStatus === "not_serviceable" || pincodeStatus === "error") && (
            <div className="flex items-center gap-1.5 text-xs font-bold text-[#ea580c] pt-1">
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-800">
                <span>⚠️</span>
                <span>Delivery not available for this pincode — change address</span>
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setIsManagingAddress(true)}
          className="shrink-0 self-start sm:self-center rounded-lg border border-[#046a38] px-4 py-2 text-xs sm:text-sm font-semibold text-[#046a38] transition hover:bg-emerald-50 active:scale-95"
        >
          Manage Address
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="h-5 w-5 text-[#046a38]" />
          <h2 className="text-base font-bold text-gray-900">Delivery Address</h2>
        </div>
        <button
          onClick={() => setAddressExpanded(!addressExpanded)}
          className="text-gray-500 hover:text-gray-700 p-1"
          aria-label="Toggle address"
        >
          {addressExpanded ? <FaChevronUp className="h-4 w-4" /> : <FaChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {addressExpanded && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          {renderContent()}
        </div>
      )}

      {editingAddress && (
        <CheckoutEditModal
          address={editingAddress}
          loading={addressesLoading}
          onSubmit={handleEditSubmit}
          onCancel={() => setEditingAddress(null)}
        />
      )}
    </div>
  );
};

export default CartAddressCard;
